import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMenu, FiX, FiChevronDown } = FiIcons;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isHomePage = location.pathname === '/';
  const isLegalPage = ['/terms-of-service', '/privacy-policy', '/cookie-policy'].includes(location.pathname);

  const scrollToSection = (sectionId) => {
    if (isHomePage) {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Navigate to homepage first, then scroll to section
      navigate('/', { replace: false });
      // Use setTimeout to ensure navigation completes before scrolling
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
    setIsOpen(false);
  };

  // For legal pages, always use dark navbar styling
  const useScrolledStyling = scrolled || isLegalPage;

  const services = [
    { name: 'Web Development', slug: 'web-development' },
    { name: 'Digital Marketing', slug: 'digital-marketing' },
    { name: 'Brand Design', slug: 'brand-design' },
    { name: 'Mobile Solutions', slug: 'mobile-solutions' },
    { name: 'E-commerce', slug: 'e-commerce' },
    { name: 'Consulting', slug: 'consulting' }
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        useScrolledStyling
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link
              to="/"
              className={`text-2xl font-bold ${
                useScrolledStyling ? 'text-slate-900' : 'text-white'
              }`}
            >
              <img src="../images/logo4.png" alt="WeDesignEdit Logo" className="h-20 w-auto" />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline space-x-8">
              {/* Home Link - Always visible */}
              <Link
                to="/"
                className={`px-3 py-2 text-sm font-medium transition-colors duration-300 ${
                  isHomePage
                    ? 'text-orange-600'
                    : useScrolledStyling
                    ? 'text-slate-700 hover:text-orange-600'
                    : 'text-slate-200 hover:text-white'
                }`}
              >
                Home
              </Link>

              {/* Services Dropdown */}
              <div
                className="relative group"
                onMouseEnter={() => setServicesDropdownOpen(true)}
                onMouseLeave={() => setServicesDropdownOpen(false)}
              >
                <button
                  onClick={() => isHomePage && scrollToSection('services')}
                  className={`px-3 py-2 text-sm font-medium transition-colors duration-300 flex items-center space-x-1 ${
                    location.pathname.startsWith('/services/')
                      ? 'text-orange-600'
                      : useScrolledStyling
                      ? 'text-slate-700 hover:text-orange-600'
                      : 'text-slate-200 hover:text-white'
                  }`}
                >
                  <span>Services</span>
                  <SafeIcon icon={FiChevronDown} className="text-xs" />
                </button>

                {/* Dropdown Menu with bridge */}
                {servicesDropdownOpen && (
                  <div className="absolute top-full left-0 pt-2 z-50">
                    {/* Invisible bridge to prevent gap issues */}
                    <div className="w-56 h-2 bg-transparent" />
                    <div className="w-56 bg-white rounded-lg shadow-xl border border-slate-200 py-2">
                      {services.map((service) => (
                        <Link
                          key={service.slug}
                          to={`/services/${service.slug}`}
                          className={`block px-4 py-2 text-sm transition-colors duration-200 ${
                            location.pathname === `/services/${service.slug}`
                              ? 'bg-orange-50 text-orange-600'
                              : 'text-slate-700 hover:bg-orange-50 hover:text-orange-600'
                          }`}
                        >
                          {service.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Link
                to="/projects"
                className={`px-3 py-2 text-sm font-medium transition-colors duration-300 ${
                  location.pathname === '/projects' || location.pathname.startsWith('/projects/')
                    ? 'text-orange-600'
                    : useScrolledStyling
                    ? 'text-slate-700 hover:text-orange-600'
                    : 'text-slate-200 hover:text-white'
                }`}
              >
                Our Work
              </Link>

              {/* <Link
                to="/service-areas"
                className={`px-3 py-2 text-sm font-medium transition-colors duration-300 ${
                  location.pathname === '/service-areas' || location.pathname.startsWith('/service-areas/')
                    ? 'text-orange-600'
                    : useScrolledStyling
                    ? 'text-slate-700 hover:text-orange-600'
                    : 'text-slate-200 hover:text-white'
                }`}
              >
                Service Areas
              </Link> */}

              {/* About - Now available on all pages */}
              <button
                onClick={() => scrollToSection('about')}
                className={`px-3 py-2 text-sm font-medium transition-colors duration-300 ${
                  useScrolledStyling
                    ? 'text-slate-700 hover:text-orange-600'
                    : 'text-slate-200 hover:text-white'
                }`}
              >
                About
              </button>
            </div>
          </div>

          {/* CTA Button */}
          <div className="hidden md:flex items-center space-x-4">
            <Link
              to="/contact"
              className="bg-orange-500 text-white px-6 py-3 rounded-full font-medium text-sm hover:bg-orange-600 transition-all duration-300 shadow-lg"
            >
              Contact Us
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`p-2 rounded-md ${
                useScrolledStyling ? 'text-slate-700' : 'text-white'
              }`}
            >
              <SafeIcon icon={isOpen ? FiX : FiMenu} className="text-xl" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="md:hidden bg-white shadow-lg max-h-screen"
        >
          <div className="max-h-[calc(100vh-5rem)] overflow-y-auto px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {/* Home Link - Always visible on mobile */}
            <Link
              to="/"
              className={`block px-3 py-2 text-base font-medium transition-colors duration-300 ${
                isHomePage
                  ? 'text-orange-600'
                  : 'text-slate-700 hover:text-orange-600'
              }`}
              onClick={() => setIsOpen(false)}
            >
              Home
            </Link>

            <div className="block px-3 py-2 text-base font-medium text-slate-700">
              Services
            </div>

            {/* Mobile Services Submenu */}
            <div className="pl-6 space-y-1">
              {services.map((service) => (
                <Link
                  key={service.slug}
                  to={`/services/${service.slug}`}
                  className={`block px-3 py-2 text-sm transition-colors duration-300 ${
                    location.pathname === `/services/${service.slug}`
                      ? 'text-orange-600'
                      : 'text-slate-600 hover:text-orange-600'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  {service.name}
                </Link>
              ))}
            </div>

            <Link
              to="/projects"
              className={`block px-3 py-2 text-base font-medium transition-colors duration-300 ${
                location.pathname === '/projects' || location.pathname.startsWith('/projects/')
                  ? 'text-orange-600'
                  : 'text-slate-700 hover:text-orange-600'
              }`}
              onClick={() => setIsOpen(false)}
            >
              Our Work
            </Link>

            {/* <Link
              to="/service-areas"
              className={`block px-3 py-2 text-base font-medium transition-colors duration-300 ${
                location.pathname === '/service-areas' || location.pathname.startsWith('/service-areas/')
                  ? 'text-orange-600'
                  : 'text-slate-700 hover:text-orange-600'
              }`}
              onClick={() => setIsOpen(false)}
            >
              Service Areas
            </Link> */}

            {/* About - Now available on all pages in mobile too */}
            <button
              onClick={() => scrollToSection('about')}
              className="block px-3 py-2 text-base font-medium text-slate-700 hover:text-orange-600 transition-colors duration-300 w-full text-left"
            >
              About
            </button>

            <div className="pt-4 pb-2">
              <Link
                to="/contact"
                className="w-full bg-orange-500 text-white px-6 py-3 rounded-full font-medium text-sm hover:bg-orange-600 transition-colors duration-300 block text-center"
                onClick={() => setIsOpen(false)}
              >
                Contact Us
              </Link>
            </div>
          </div>
        </motion.div>
      )}
    </motion.nav>
  );
};

export default Navbar;